import { FC, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import { AlertTriangle, RefreshCcw, Clock, CheckCircle2, XCircle, HelpCircle } from "lucide-react";

import { api } from "@/modules/api";
import { StatCard } from "@/components/metrics/stat-card";
import { Button } from "@/components/button";
import { atlasRoutes } from "@/app/router/routes";
import { ImportStatus } from "@/pages/imports/shared/import-status";

interface ImportSessionSchema {
  id: string;
  owned_by_space_account_id: string;
  title?: string | null;
  status?: string | null;
  kind?: string | null;
  internal_created_at?: string | null;
  uploaded_file_batch?: {
    id?: string;
    imported_note_count?: number;
    imported_collection_count?: number;
  } | null;
}

interface SessionEventSummarySchema {
  id: string;
  session_id: string;
  internal_created_at: string;
  data?: {
    kind?: string;
    value?: {
      input_kind?: string;
    };
  };
}

interface DashboardStats {
  processing: number;
  awaitingInput: number;
  failed24h: number;
  finished24h: number;
  successRate: number;
  totalNotes: number;
  totalCollections: number;
}

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

export const ImportsDashboardPage: FC = () => {
  const navigate = useNavigate();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(DateTime.now());

  // Fetch all active sessions (non-dismissed)
  const {
    data: allSessionsData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["import-sessions-dashboard"],
    queryFn: () =>
      api.post("/internal/v1/import-sessions/search", {
        body: {
          filter_by_kind: "IMPORT",
          limit: 500,
        },
      }),
    staleTime: AUTO_REFRESH_INTERVAL,
    refetchInterval: autoRefresh ? AUTO_REFRESH_INTERVAL : false,
  });

  // Fetch session events for sessions awaiting input
  const allSessions = (allSessionsData?.data?.results ?? []) as ImportSessionSchema[];
  const processingSessions = allSessions.filter(s => s.status === "PROCESSING");

  const { data: inputRequestedData } = useQuery({
    queryKey: ["session-events-input-requested", processingSessions.map(s => s.id)],
    queryFn: async () => {
      if (processingSessions.length === 0) return { awaiting: [] };

      // Fetch INPUT_REQUESTED and INPUT_PROVIDED events for all processing sessions
      const results = await Promise.all(
        processingSessions.map(async session => {
          const [requested, provided] = await Promise.all([
            api.post("/internal/v1/session-events/search", {
              body: {
                filter_by_session_id: session.id,
                filter_by_event_kind: "INPUT_REQUESTED",
                limit: 50,
              },
            }),
            api.post("/internal/v1/session-events/search", {
              body: {
                filter_by_session_id: session.id,
                filter_by_event_kind: "INPUT_PROVIDED",
                limit: 50,
              },
            }),
          ]);

          const requestedEvents = (requested.data?.results ?? []) as SessionEventSummarySchema[];
          const providedEvents = (provided.data?.results ?? []) as SessionEventSummarySchema[];

          // Calculate if session is awaiting input using FIFO logic
          const byTimeAsc = (a: SessionEventSummarySchema, b: SessionEventSummarySchema) =>
            new Date(a.internal_created_at).getTime() - new Date(b.internal_created_at).getTime();

          const requestedSorted = [...requestedEvents].sort(byTimeAsc);
          const providedSorted = [...providedEvents].sort(byTimeAsc);

          const unansweredQueue: SessionEventSummarySchema[] = [];
          let i = 0;
          let j = 0;
          while (i < requestedSorted.length || j < providedSorted.length) {
            const nextRequestedAt =
              i < requestedSorted.length
                ? new Date(requestedSorted[i].internal_created_at).getTime()
                : Number.POSITIVE_INFINITY;
            const nextProvidedAt =
              j < providedSorted.length
                ? new Date(providedSorted[j].internal_created_at).getTime()
                : Number.POSITIVE_INFINITY;

            if (nextRequestedAt <= nextProvidedAt) {
              unansweredQueue.push(requestedSorted[i]);
              i += 1;
            } else {
              if (unansweredQueue.length > 0) {
                unansweredQueue.shift();
              }
              j += 1;
            }
          }

          const isAwaiting = unansweredQueue.length > 0;
          const oldestUnanswered = unansweredQueue[0];

          return {
            session,
            isAwaiting,
            oldestUnanswered,
            unansweredCount: unansweredQueue.length,
          };
        })
      );

      return {
        awaiting: results.filter(r => r.isAwaiting),
      };
    },
    enabled: processingSessions.length > 0,
    staleTime: AUTO_REFRESH_INTERVAL,
    refetchInterval: autoRefresh ? AUTO_REFRESH_INTERVAL : false,
  });

  // Calculate stats
  const stats: DashboardStats = useMemo(() => {
    const now = DateTime.now();
    const last24h = now.minus({ hours: 24 });

    const processing = allSessions.filter(s => s.status === "PROCESSING").length;
    const awaitingInput = inputRequestedData?.awaiting.length ?? 0;

    const recentSessions = allSessions.filter(s => {
      if (!s.internal_created_at) return false;
      const createdAt = DateTime.fromISO(s.internal_created_at);
      return createdAt >= last24h;
    });

    const failed24h = recentSessions.filter(s => s.status === "ERROR").length;
    const finished24h = recentSessions.filter(s => s.status === "FINISHED").length;

    const total24h = failed24h + finished24h;
    const successRate = total24h > 0 ? Math.round((finished24h / total24h) * 100) : 0;

    const totalNotes = allSessions
      .filter(s => s.status === "FINISHED")
      .reduce((sum, s) => sum + (s.uploaded_file_batch?.imported_note_count ?? 0), 0);

    const totalCollections = allSessions
      .filter(s => s.status === "FINISHED")
      .reduce((sum, s) => sum + (s.uploaded_file_batch?.imported_collection_count ?? 0), 0);

    return {
      processing,
      awaitingInput,
      failed24h,
      finished24h,
      successRate,
      totalNotes,
      totalCollections,
    };
  }, [allSessions, inputRequestedData]);

  // Sessions to display in tables
  const processingSessionsList = useMemo(() => {
    return processingSessions
      .map(session => {
        const createdAt = session.internal_created_at
          ? DateTime.fromISO(session.internal_created_at)
          : null;
        const age = createdAt ? DateTime.now().diff(createdAt) : null;
        const ageMinutes = age ? Math.floor(age.as("minutes")) : null;

        return {
          ...session,
          createdAt,
          ageMinutes,
        };
      })
      .sort((a, b) => (b.ageMinutes ?? 0) - (a.ageMinutes ?? 0));
  }, [processingSessions]);

  const awaitingInputList = useMemo(() => {
    return (inputRequestedData?.awaiting ?? [])
      .map(item => {
        const waitTime = item.oldestUnanswered
          ? DateTime.now().diff(DateTime.fromISO(item.oldestUnanswered.internal_created_at))
          : null;
        const waitMinutes = waitTime ? Math.floor(waitTime.as("minutes")) : null;

        return {
          ...item,
          waitMinutes,
        };
      })
      .sort((a, b) => (b.waitMinutes ?? 0) - (a.waitMinutes ?? 0));
  }, [inputRequestedData]);

  const recentFailures = useMemo(() => {
    const last24h = DateTime.now().minus({ hours: 24 });
    return allSessions
      .filter(s => {
        if (s.status !== "ERROR") return false;
        if (!s.internal_created_at) return false;
        const createdAt = DateTime.fromISO(s.internal_created_at);
        return createdAt >= last24h;
      })
      .sort((a, b) => {
        const aTime = a.internal_created_at
          ? DateTime.fromISO(a.internal_created_at).toMillis()
          : 0;
        const bTime = b.internal_created_at
          ? DateTime.fromISO(b.internal_created_at).toMillis()
          : 0;
        return bTime - aTime;
      })
      .slice(0, 10);
  }, [allSessions]);

  // Handle manual refresh
  const handleRefresh = () => {
    refetch();
    setLastRefresh(DateTime.now());
  };

  // Update last refresh time when data changes
  useEffect(() => {
    if (!isLoading) {
      setLastRefresh(DateTime.now());
    }
  }, [allSessionsData, isLoading]);

  if (isLoading) {
    return <StateMessage>Loading imports dashboard…</StateMessage>;
  }

  if (isError) {
    return (
      <StateMessage>
        <AlertTriangle size={18} />
        <span>Unable to load imports dashboard</span>
        <Button variant="outline" size="1" onClick={handleRefresh}>
          <ButtonContent>
            <RefreshCcw size={14} />
            <span>Retry</span>
          </ButtonContent>
        </Button>
      </StateMessage>
    );
  }

  return (
    <PageContainer>
      <HeaderRow>
        <div>
          <PageTitle>Imports Dashboard</PageTitle>
          <Subtitle>
            Monitor import sessions and track issues •{" "}
            <BackLink onClick={() => navigate(atlasRoutes.imports({}).path)}>
              View all imports
            </BackLink>
          </Subtitle>
        </div>
        <Actions>
          <AutoRefreshToggle>
            <input
              type="checkbox"
              id="auto-refresh"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
            />
            <label htmlFor="auto-refresh">Auto-refresh</label>
          </AutoRefreshToggle>
          <LastUpdated>
            Last updated: {lastRefresh.toLocaleString(DateTime.TIME_WITH_SECONDS)}
          </LastUpdated>
          <Button variant="outline" size="1" onClick={handleRefresh}>
            <ButtonContent>
              <RefreshCcw size={14} />
              <span>Refresh</span>
            </ButtonContent>
          </Button>
        </Actions>
      </HeaderRow>

      <StatsGrid>
        <StatCard
          label="Active Processing"
          value={stats.processing}
          tone={stats.processing > 10 ? "warning" : "default"}
          description={`${stats.awaitingInput} awaiting user input`}
          icon={<Clock size={16} color={stats.processing > 10 ? "#ad5f00" : "#667085"} />}
        />
        <StatCard
          label="Awaiting Input"
          value={stats.awaitingInput}
          tone={stats.awaitingInput > 5 ? "warning" : "default"}
          description="Pending user response"
          icon={<HelpCircle size={16} color={stats.awaitingInput > 5 ? "#ad5f00" : "#667085"} />}
        />
        <StatCard
          label="Failed (24h)"
          value={stats.failed24h}
          tone={stats.failed24h > 0 ? "danger" : "default"}
          description={`${stats.finished24h} finished`}
          icon={<XCircle size={16} color={stats.failed24h > 0 ? "#d92d20" : "#667085"} />}
        />
        <StatCard
          label="Success Rate (24h)"
          value={`${stats.successRate}%`}
          tone={stats.successRate < 80 ? "danger" : stats.successRate < 95 ? "warning" : "success"}
          description={`${stats.finished24h + stats.failed24h} total imports`}
          icon={
            <CheckCircle2
              size={16}
              color={
                stats.successRate < 80 ? "#d92d20" : stats.successRate < 95 ? "#ad5f00" : "#18794e"
              }
            />
          }
        />
      </StatsGrid>

      <StatsGrid>
        <StatCard
          label="Total Notes Imported"
          value={stats.totalNotes.toLocaleString()}
          description="All finished imports"
        />
        <StatCard
          label="Total Collections Imported"
          value={stats.totalCollections.toLocaleString()}
          description="All finished imports"
        />
      </StatsGrid>

      {/* Processing Sessions Table */}
      {processingSessionsList.length > 0 && (
        <TableCard>
          <SectionTitle>Processing Sessions ({processingSessionsList.length})</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Age</Th>
                  <Th>Title</Th>
                  <Th>Space Account</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {processingSessionsList.map(session => (
                  <tr key={session.id}>
                    <Td>
                      <AgeCell $warning={session.ageMinutes ? session.ageMinutes > 60 : false}>
                        {session.ageMinutes
                          ? session.ageMinutes < 60
                            ? `${session.ageMinutes}m`
                            : `${Math.floor(session.ageMinutes / 60)}h ${session.ageMinutes % 60}m`
                          : "—"}
                      </AgeCell>
                    </Td>
                    {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                    <Td>{session.title || "Untitled"}</Td>
                    <Td>
                      <MonoText>{session.owned_by_space_account_id.slice(0, 8)}...</MonoText>
                    </Td>
                    <Td>
                      <StatusBadge $status="PROCESSING">Processing</StatusBadge>
                    </Td>
                    <Td>
                      <Button
                        variant="outline"
                        size="1"
                        onClick={() =>
                          navigate(
                            atlasRoutes.importsView({ params: { importSessionId: session.id } })
                              .path
                          )
                        }
                      >
                        View
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </TableCard>
      )}

      {/* Awaiting Input Table */}
      {awaitingInputList.length > 0 && (
        <TableCard>
          <SectionTitle>Awaiting User Input ({awaitingInputList.length})</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Wait Time</Th>
                  <Th>Title</Th>
                  <Th>Unanswered</Th>
                  <Th>Space Account</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {awaitingInputList.map(item => (
                  <tr key={item.session.id}>
                    <Td>
                      <AgeCell $warning={item.waitMinutes ? item.waitMinutes > 120 : false}>
                        {item.waitMinutes
                          ? item.waitMinutes < 60
                            ? `${item.waitMinutes}m`
                            : `${Math.floor(item.waitMinutes / 60)}h ${item.waitMinutes % 60}m`
                          : "—"}
                      </AgeCell>
                    </Td>
                    {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                    <Td>{item.session.title || "Untitled"}</Td>
                    <Td>{item.unansweredCount} question(s)</Td>
                    <Td>
                      <MonoText>{item.session.owned_by_space_account_id.slice(0, 8)}...</MonoText>
                    </Td>
                    <Td>
                      <Button
                        variant="outline"
                        size="1"
                        onClick={() =>
                          navigate(
                            atlasRoutes.importsView({
                              params: { importSessionId: item.session.id },
                            }).path
                          )
                        }
                      >
                        View
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </TableCard>
      )}

      {/* Recent Failures Table */}
      {recentFailures.length > 0 && (
        <TableCard>
          <SectionTitle>Recent Failures (Last 24h)</SectionTitle>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Created</Th>
                  <Th>Title</Th>
                  <Th>Space Account</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {recentFailures.map(session => (
                  <tr key={session.id}>
                    <Td>
                      {session.internal_created_at
                        ? DateTime.fromISO(session.internal_created_at).toRelative({ locale: "en" })
                        : "—"}
                    </Td>
                    {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
                    <Td>{session.title || "Untitled"}</Td>
                    <Td>
                      <MonoText>{session.owned_by_space_account_id.slice(0, 8)}...</MonoText>
                    </Td>
                    <Td>
                      <StatusBadge $status="ERROR">Error</StatusBadge>
                    </Td>
                    <Td>
                      <Button
                        variant="outline"
                        size="1"
                        onClick={() =>
                          navigate(
                            atlasRoutes.importsView({ params: { importSessionId: session.id } })
                              .path
                          )
                        }
                      >
                        View
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </TableCard>
      )}

      {processingSessionsList.length === 0 &&
        awaitingInputList.length === 0 &&
        recentFailures.length === 0 && (
          <EmptyState>
            <CheckCircle2 size={48} color="var(--green-9)" />
            <h3>All Clear!</h3>
            <p>No active processing sessions or recent failures.</p>
          </EmptyState>
        )}
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  color: var(--gray-11);
  font-size: 14px;
`;

const BackLink = styled.span`
  color: var(--blue-11);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--blue-12);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const AutoRefreshToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  input {
    cursor: pointer;
  }

  label {
    cursor: pointer;
    font-size: 12px;
    color: var(--gray-11);
  }
`;

const LastUpdated = styled.span`
  color: var(--gray-11);
  font-size: 12px;
  white-space: nowrap;
`;

const StatsGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
`;

const TableCard = styled.div`
  border: 1px solid var(--gray-4);
  border-radius: 12px;
  padding: 20px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    text-align: left;
    padding: 12px;
    border-bottom: 1px solid var(--gray-4);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: var(--gray-2);
  }
`;

const Th = styled.th`
  font-weight: 600;
  color: var(--gray-12);
  background: var(--gray-1);
`;

const Td = styled.td`
  color: var(--gray-11);
`;

const MonoText = styled.span`
  font-family: monospace;
  font-size: 12px;
`;

const AgeCell = styled.span<{ $warning: boolean }>`
  color: ${props => (props.$warning ? "var(--red-11)" : "inherit")};
  font-weight: ${props => (props.$warning ? "600" : "normal")};
`;

const StatusBadge = styled.span<{ $status: ImportStatus }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.$status) {
      case "PROCESSING":
        return "var(--blue-3)";
      case "ERROR":
        return "var(--red-3)";
      case "FINISHED":
        return "var(--green-3)";
      default:
        return "var(--gray-3)";
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case "PROCESSING":
        return "var(--blue-11)";
      case "ERROR":
        return "var(--red-11)";
      case "FINISHED":
        return "var(--green-11)";
      default:
        return "var(--gray-11)";
    }
  }};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: var(--gray-11);

  h3 {
    margin: 16px 0 8px;
    font-size: 20px;
    color: var(--gray-12);
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

const StateMessage = styled.div`
  padding: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--gray-11);

  svg {
    flex-shrink: 0;
  }

  button {
    margin-left: 8px;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
